// src/components/Dashboard/AdminDashboard/AllUsers/RevenueSharing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./RevenueSharing.css";

// ✅ Same card component used in ContractMonitor
import AdminNeonCard from "../../../AdminNeonCard";

/**
 * ADMIN — REVENUE SHARING (UI Placeholder using Completed-Accordion look)
 *
 * Currently reuses the "COMPLETED" accordion layout from ContractMonitor
 * so you get the same look/feel while you wire real revenue logic later.
 *
 * Endpoint used (same as ContractMonitor completed feed):
 *  - GET /api/admin/contracts/completed
 */

// ✅ Backend base (dev/prod safe)
const BACKEND_ORIGIN = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

// ---------- Helpers copied/adapted from ContractMonitor (safe in this file) ----------
const cleanName = (v) => {
    const s = String(v ?? "").trim();
    return s.length ? s : null;
};

const resolvePlayerProfileSlug = (raw) => {
    return cleanName(raw?.playerProfileSlug) || cleanName(raw?.PlayerDatum?.profileSlug) || null;
};

const resolvePartnerProfileSlug = (raw) => {
    return (
        cleanName(raw?.partnerProfileSlug) ||
        cleanName(raw?.Agent?.profileSlug) ||
        cleanName(raw?.Scout?.profileSlug) ||
        cleanName(raw?.Club?.profileSlug) ||
        null
    );
};

const resolveContractUrl = (url) => {
    if (!url) return null;
    const s = String(url).trim();
    if (!s) return null;

    // absolute URL (fix localhost:3000/uploads -> backend origin)
    if (/^https?:\/\//i.test(s)) {
        try {
            const u = new URL(s);
            if (
                (u.hostname === "localhost" || u.hostname === "127.0.0.1") &&
                u.port === "3000" &&
                u.pathname.startsWith("/uploads/")
            ) {
                return `${BACKEND_ORIGIN}${u.pathname}${u.search || ""}${u.hash || ""}`;
            }
            return s;
        } catch {
            return s;
        }
    }

    // relative uploads path
    if (s.startsWith("/uploads/")) return `${BACKEND_ORIGIN}${s}`;

    return s;
};

// ✅ Group completed contracts by completion day
const startOfLocalDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const formatDayHeader = (dateObj) => {
    const weekday = dateObj.toLocaleDateString(undefined, { weekday: "long" });
    const rest = dateObj.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    return `${weekday} • ${rest}`;
};

const getCompletedAtMs = (raw) => {
    const t = raw?.completedAt || raw?.expiresAt || raw?.terminatedAt || raw?.updatedAt || raw?.createdAt || null;
    if (!t) return null;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
};

// Minimal normalization so AdminNeonCard + buttons work without bringing the whole monitor
const normalizeForCard = (raw) => {
    const completionType = String(raw?.completionType || "").toLowerCase().trim();
    const status = String(raw?.status || "").toLowerCase().trim();

    const uiContractTitle =
        completionType === "expired"
            ? "EXPIRED CONTRACT"
            : completionType === "terminated"
                ? "TERMINATED CONTRACT"
                : status === "terminated"
                    ? "TERMINATED CONTRACT"
                    : "COMPLETED CONTRACT";

    return {
        id: raw?.id,

        scoutName: cleanName(raw?.scoutName) || "—",
        playerName: cleanName(raw?.playerName || raw?.playerFullName) || "—",
        playerUsername: cleanName(raw?.playerUsername) || "—",

        agentName: cleanName(raw?.agentName),
        clubName: cleanName(raw?.clubName),

        playerProfileSlug: resolvePlayerProfileSlug(raw),
        partnerProfileSlug: resolvePartnerProfileSlug(raw),

        agentContractUrl: raw?.agentContractUrl || null,

        contractDuration: cleanName(raw?.contractDuration) || "—",

        uiContractTitle,
        statusText: completionType || status || "completed",
        badge: { subtitle: "Status", title: "COMPLETED" },

        _raw: raw,
    };
};
// ---------- End helpers ----------

const RevenueSharing = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Using completed contracts feed as the base list for now
    const [completedContracts, setCompletedContracts] = useState([]);

    // ✅ “LIVE” heartbeat (tiny dot pulse) — optional, keeps same feel
    const [beat, setBeat] = useState(false);

    // ✅ Modal view (same pattern)
    const [selected, setSelected] = useState(null);

    // ✅ Completed-day accordion state
    const [openDays, setOpenDays] = useState(() => new Set());

    // ✅ NEW: Email list toggle per day
    const [openEmailDays, setOpenEmailDays] = useState(() => new Set());

    const toggleDay = (dayKey) => {
        setOpenDays((prev) => {
            const next = new Set(prev);
            if (next.has(dayKey)) next.delete(dayKey);
            else next.add(dayKey);
            return next;
        });
    };

    // ✅ NEW: toggle email list inside a day
    const toggleEmailDay = (dayKey) => {
        setOpenEmailDays((prev) => {
            const next = new Set(prev);
            if (next.has(dayKey)) next.delete(dayKey);
            else next.add(dayKey);
            return next;
        });
    };

    const copyEmails = async (emails) => {
        try {
            const text = (emails || []).join("\n");
            await navigator.clipboard.writeText(text);
        } catch {
            // ignore clipboard failures
        }
    };

    // Fetch completed feed
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");

            try {
                const res = await apiFetch(`/api/admin/contracts/completed`, { method: "GET" });
                if (!res.ok) throw new Error("Failed to fetch completed contracts.");

                const txt = await res.text().catch(() => "");
                let data = [];
                try {
                    data = txt ? JSON.parse(txt) : [];
                } catch {
                    data = [];
                }

                if (cancelled) return;
                setCompletedContracts(Array.isArray(data) ? data : []);
            } catch (e) {
                if (cancelled) return;
                setError(e?.message || "Failed to load revenue sharing data.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        // refresh every 20s (same cadence)
        const id = setInterval(load, 20000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    // “live” heartbeat
    useEffect(() => {
        const id = setInterval(() => setBeat((b) => !b), 900);
        return () => clearInterval(id);
    }, []);

    // ESC to close modal
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setSelected(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Group by day (same as ContractMonitor completed)
    const groups = useMemo(() => {
        const buckets = new Map();

        (completedContracts || []).forEach((raw) => {
            const ms = getCompletedAtMs(raw);
            if (!ms) return;

            const dayStart = startOfLocalDay(ms);
            const dayKey = String(dayStart.getTime());

            if (!buckets.has(dayKey)) {
                buckets.set(dayKey, {
                    dayStartMs: dayStart.getTime(),
                    label: formatDayHeader(dayStart),
                    items: [],
                });
            }
            buckets.get(dayKey).items.push(raw);
        });

        return Array.from(buckets.values())
            .sort((a, b) => b.dayStartMs - a.dayStartMs)
            .map((g) => ({
                ...g,
                items: g.items.sort((a, b) => (getCompletedAtMs(b) || 0) - (getCompletedAtMs(a) || 0)),
            }));
    }, [completedContracts]);

    const openInNewTab = (url) => {
        const finalUrl = resolveContractUrl(url);
        if (!finalUrl) return;
        window.open(finalUrl, "_blank", "noopener,noreferrer");
    };

    const goTo = (url) => {
        if (!url) return;
        window.location.href = url;
    };

    return (
        <div className="revenue-sharing">
            <div
                className="revenue-sharing-header"
                style={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    marginBottom: 18,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                    <h1 style={{ margin: 0, color: "yellow" }}>Revenue Sharing</h1>
                    <span
                        title="Live feed"
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: beat ? "#39ff14" : "#1a3b14",
                            boxShadow: beat ? "0 0 10px rgba(57,255,20,0.8)" : "none",
                            display: "inline-block",
                        }}
                    />
                </div>

                <p style={{ margin: 0, color: "#bbb" }}>
                    These are players whose contract has ended on specified dates and are due for Revenue Sharing...We have "PlayerEarnings" table in database we'll incorporate in future. For now it is just a concept.
                </p>
            </div>

            {loading ? (
                <p style={{ color: "#bbb" }}>Loading...</p>
            ) : error ? (
                <p style={{ color: "salmon" }}>{error}</p>
            ) : groups.length === 0 ? (
                <p style={{ color: "#bbb" }}>No completed contracts to display.</p>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {groups.map((group) => {
                        const dayKey = String(group.dayStartMs);
                        const isOpen = openDays.has(dayKey);
                        const emailsOpen = openEmailDays.has(dayKey);

                        // ✅ NEW: collect all login emails for that day (deduped)
                        const emailsForDay = Array.from(
                            new Set((group.items || []).map((x) => cleanName(x?.playerLoginEmail)).filter(Boolean))
                        );

                        return (
                            <div
                                key={dayKey}
                                style={{
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 14,
                                    background: "rgba(255,255,255,0.02)",
                                    overflow: "hidden",
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleDay(dayKey)}
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        padding: "12px 14px",
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#fff",
                                    }}
                                    title="Toggle day"
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontWeight: 800 }}>{group.label}</span>
                                        <span style={{ color: "#bbb" }}>({group.items.length})</span>
                                    </div>

                                    <span
                                        style={{
                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 120ms ease",
                                            color: "#39ff14",
                                            fontWeight: 900,
                                        }}
                                    >
                                        ▼
                                    </span>
                                </button>

                                {isOpen ? (
                                    <div style={{ padding: 12 }}>
                                        {/* ✅ NEW: Email payout helper controls */}
                                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                                            <button
                                                type="button"
                                                className="btn view"
                                                onClick={() => toggleEmailDay(dayKey)}
                                                title="Show login emails for payouts"
                                            >
                                                {emailsOpen ? "Hide Player Login Emails" : "Show Player Login Emails"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn view"
                                                onClick={() => copyEmails(emailsForDay)}
                                                disabled={emailsForDay.length === 0}
                                                title="Copy all emails in this day"
                                            >
                                                Copy Emails
                                            </button>

                                            <span style={{ color: "#bbb", alignSelf: "center" }}>
                                                Emails: {emailsForDay.length}
                                            </span>
                                        </div>

                                        {emailsOpen ? (
                                            <div
                                                style={{
                                                    border: "1px solid rgba(255,255,255,0.08)",
                                                    borderRadius: 12,
                                                    padding: 12,
                                                    background: "rgba(0,0,0,0.25)",
                                                    marginBottom: 12,
                                                }}
                                            >
                                                {emailsForDay.length === 0 ? (
                                                    <div style={{ color: "#bbb" }}>
                                                        No player login emails found for this day.
                                                    </div>
                                                ) : (
                                                    <ul style={{ margin: 0, paddingLeft: 18, color: "#fff" }}>
                                                        {emailsForDay.map((email) => (
                                                            <li key={email} style={{ marginBottom: 6 }}>
                                                                {email}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        ) : null}

                                        {/* Existing cards grid (unchanged) */}
                                        <div className="contracts-grid">
                                            {group.items.map((raw) => {
                                                const c = normalizeForCard(raw);

                                                const playerSlug = c?.playerProfileSlug;
                                                const partnerSlug = c?.partnerProfileSlug;

                                                const playerProfileHref = playerSlug ? `/player-profile/${playerSlug}` : null;

                                                const partnerRole = String(c?._raw?.signedByRole || "").toLowerCase();
                                                const partnerProfileHref =
                                                    partnerSlug && partnerRole === "scout"
                                                        ? `/scout-profile/${partnerSlug}`
                                                        : partnerSlug && partnerRole === "agent"
                                                            ? `/agent-profile/${partnerSlug}`
                                                            : partnerSlug && partnerRole === "club"
                                                                ? `/club-profile/${partnerSlug}`
                                                                : null;

                                                return (
                                                    <AdminNeonCard key={c.id || `${c.playerUsername}`} contract={c}>
                                                        <div className="card-actions">
                                                            <button className="btn view" onClick={() => setSelected(c._raw)}>
                                                                View
                                                            </button>

                                                            {c?.agentContractUrl ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn view"
                                                                    onClick={() => openInNewTab(c.agentContractUrl)}
                                                                    title="Open uploaded contract"
                                                                >
                                                                    View Contract
                                                                </button>
                                                            ) : null}

                                                            {playerProfileHref ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn view"
                                                                    onClick={() => goTo(playerProfileHref)}
                                                                    title="Open player profile"
                                                                >
                                                                    Player Profile
                                                                </button>
                                                            ) : null}

                                                            {partnerProfileHref ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn view"
                                                                    onClick={() => goTo(partnerProfileHref)}
                                                                    title="Open partner profile"
                                                                >
                                                                    Partner Profile
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </AdminNeonCard>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}

            {selected ? <ContractDetailsModal contract={selected} onClose={() => setSelected(null)} /> : null}
        </div>
    );
};

export default RevenueSharing;

// ---------- Modal copied (same as ContractMonitor) ----------
function ContractDetailsModal({ contract, onClose }) {
    if (!contract) return null;

    const contractId = contract?.id ?? "—";

    const fmt = (d) => {
        if (!d) return "—";
        const t = new Date(d);
        return Number.isNaN(t.getTime()) ? "—" : t.toLocaleString();
    };

    const copyId = async () => {
        try {
            await navigator.clipboard.writeText(String(contractId));
        } catch {
            // ignore
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.72)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(980px, 96vw)",
                    maxHeight: "90vh",
                    overflow: "auto",
                    background: "#070707",
                    border: "1px solid rgba(57,255,20,0.35)",
                    boxShadow: "0 0 24px rgba(57,255,20,0.18)",
                    borderRadius: 14,
                    padding: 18,
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                        <div style={{ color: "#39ff14", fontWeight: 800, letterSpacing: 1 }}>CONTRACT DETAILS</div>

                        <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ color: "#bbb" }}>Contract ID:</span>
                            <span style={{ color: "#fff" }}>{contractId}</span>
                            <button
                                onClick={copyId}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(57,255,20,0.35)",
                                    color: "#39ff14",
                                    padding: "4px 10px",
                                    borderRadius: 10,
                                    cursor: "pointer",
                                }}
                                title="Copy Contract ID"
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        style={{
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.15)",
                            color: "#fff",
                            padding: "6px 12px",
                            borderRadius: 10,
                            cursor: "pointer",
                            height: 34,
                        }}
                    >
                        Close
                    </button>
                </div>

                <div
                    style={{
                        marginTop: 16,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 14,
                    }}
                >
                    <DetailRow label="Signed By Role" value={contract?.signedByRole ?? "—"} />
                    <DetailRow label="Status" value={contract?.status ?? "—"} />

                    <DetailRow label="Admin Approval" value={contract?.adminApproval ?? "—"} />
                    <DetailRow label="Admin Approval Time" value={fmt(contract?.adminApprovalTime)} />

                    <DetailRow label="Signed By Player" value={String(!!contract?.signedByPlayer)} />
                    <DetailRow label="Payment Status" value={contract?.paymentStatus ?? "—"} />

                    <DetailRow label="Paid At" value={fmt(contract?.paidAt)} />
                    <DetailRow label="Expires At" value={contract?.expiresAt ? fmt(contract.expiresAt) : "—"} />

                    <DetailRow label="Activation Hold Expires" value={fmt(contract?.activationHoldExpiresAt)} />
                    <DetailRow label="Submitted At" value={fmt(contract?.submittedAt)} />

                    <DetailRow label="Contract Duration" value={contract?.contractDuration ?? "—"} />
                    <DetailRow label="Agree To Terms" value={String(!!contract?.agreeToTerms)} />

                    <DetailRow label="Termination Reason" value={contract?.terminationReason ?? "—"} />
                    <DetailRow label="Rejection Reason" value={contract?.rejectionReason ?? "—"} />

                    <DetailRow label="Withdraw Reason" value={contract?.withdrawReason ?? "—"} />
                    <DetailRow label="Player Note" value={contract?.playerNote ?? "—"} />

                    <DetailRow label="Stripe Session ID" value={contract?.stripeSessionId ?? "—"} />
                    <DetailRow label="Payment Intent ID" value={contract?.paymentIntentId ?? "—"} />

                    <DetailRow label="Refund ID" value={contract?.refundId ?? "—"} />
                    <DetailRow label="Refunded At" value={fmt(contract?.refundedAt)} />

                    <DetailRow label="Created At" value={fmt(contract?.createdAt)} />
                    <DetailRow label="Updated At" value={fmt(contract?.updatedAt)} />

                    <DetailRow label="IP Address" value={contract?.ipAddress ?? "—"} />
                    <DetailRow label="Notes" value={contract?.notes ?? "—"} />
                </div>
            </div>
        </div>
    );
}

function DetailRow({ label, value }) {
    const v = value === null || value === undefined || String(value).trim() === "" ? "—" : value;

    return (
        <div
            style={{
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 12,
                padding: 12,
                background: "rgba(255,255,255,0.02)",
            }}
        >
            <div style={{ color: "#bbb", fontSize: 12 }}>{label}</div>
            <div style={{ color: "#fff", marginTop: 4, wordBreak: "break-word" }}>{v}</div>
        </div>
    );
}